import { browser } from '$app/environment';
import { changeColor, getContrastYIQ } from '$lib/helper/color';
import type { BookingStatus } from '$lib/model/booking';
import type { CustomField, CustomFieldSettingsMap } from '$lib/model/custom-fields';
import type { Session } from '$lib/model/session';
import { apiStore } from '$lib/stores/api';
import { t } from '$lib/stores/i18n';
import { widget } from '$lib/stores/widget';
import {
	widgetBooking,
	widgetBookingExpiresAt,
	widgetColors,
	widgetColorsForceDarkMode,
	widgetConfig,
	widgetContent,
	type WidgetBooking
} from '$lib/widget/stores';
import type { Axios } from 'axios';
import { DateTime } from 'luxon';
import { get } from 'svelte/store';

export type WidgetColorProfile = {
	fontFamily: string;
	showLogo: boolean;
	showBusinessName: boolean;
	inversedHeaderTextColor: boolean;
	banner: string;
	buttonStyle: WidgetButtonStyle;
	primary: string;
	background: string;
	useDarkMode: boolean;
	dark: {
		primary: string;
		background: string;
		inversedHeaderTextColor: boolean;
	};
};

export type WidgetButtonStyle = 'sharp' | 'rounded' | 'circle';

export const updateWidgetTheme = (colorProfile: WidgetColorProfile) => {
	changeColor(colorProfile.primary, 'widget-primary');
	changeColor(colorProfile.background, 'widget-background');

	if (colorProfile.useDarkMode) {
		changeColor(colorProfile.dark.primary ?? colorProfile.primary, 'widget-primary-dark');
		changeColor(
			colorProfile.dark.background ?? colorProfile.background,
			'widget-background-dark'
		);
	} else {
		changeColor(colorProfile.primary, 'widget-primary-dark');
		changeColor(colorProfile.background, 'widget-background-dark');
	}
};

export const getWidgetBackgroundContrastHexColor = (colorProfile: WidgetColorProfile) => {
	return getContrastYIQ(
		colorProfile
			? colorProfile.useDarkMode && window.matchMedia('(prefers-color-scheme: dark)').matches
				? colorProfile.dark.background
				: colorProfile.background
			: '#000000'
	);
};

// Switch between light and dark mode is done by switching css colors
// However some differences exists between the two modes that are not colors, but classes (like brightness and contrast)
// This function will return the correct classes depending on the current profile
// Provide dark classes with their dark: prefix, and light classes without any prefix.
export const classesDependingOnProfile = (lightClasses: string, darkClasses: string) => {
	if (get(widgetColors).useDarkMode) {
		if (get(widgetColorsForceDarkMode)) {
			// We want to force dark mode, return only dark classes, stripped from their dark: prefix
			// to make them applied in the light theme.
			return darkClasses.replace(/dark:/g, '');
		}

		if (browser && window.matchMedia('(prefers-color-scheme: dark)').matches) {
			// Dark mode is enabled, return dark classes
			return darkClasses;
		}

		// Not in dark mode, return light classes
		return lightClasses;
	} else {
		// No dark mode, only light classes are used, in any case
		return lightClasses;
	}
};

export function getFontFamilies() {
	return ['default', 'poppins', 'robotoc', 'merriweather'];
}

export function getFontUrls(family: string) {
	// First font is for text, second for titles
	switch (family) {
		case 'poppins':
			return [
				'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100..900;1,100..900&display=swap',
				'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100..900;1,100..900&display=swap'
			];
		case 'robotoc':
			return [
				'https://fonts.googleapis.com/css2?family=Roboto+Condensed:ital,wght@0,100..900;1,100..900&display=swap',
				'https://fonts.googleapis.com/css2?family=Roboto+Condensed:ital,wght@0,100..900;1,100..900&display=swap'
			];
		case 'merriweather':
			return [
				'https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,100..900;1,100..900&display=swap',
				'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap'
			];
		default:
			return null;
	}
}

export function getFontTitle() {
	let fontFamily = get(widget)?.theme?.fontFamily;
	if (!fontFamily) {
		fontFamily = get(widgetConfig)?.theme?.fontFamily;
	}

	if (!fontFamily || fontFamily === 'default') {
		return 'font-title font-extralight';
	}

	return `font-${fontFamily}-title`;
}

export type WidgetContent = {
	business: WidgetBusiness;
	activities: Array<WidgetActivity>;
	offers: Record<string, WidgetOffer>;
	offerCollections: Record<WidgetOfferCollection['id'], WidgetOfferCollection>;
	sessions: Record<string, WidgetSession>;
	giftcards: WidgetBookingGiftcard[];
	customFields: WidgetCustomField<keyof CustomFieldSettingsMap>[];
};

export type WidgetBusiness = {
	logo: string;
	name: string;
	timezone: string;
};

export type WidgetCustomContent = {
	welcome?: string;
	thankYou?: string;
	validation?: string;
	myBooking?: string;
	reconfirmBooking?: string;
};

export type WidgetCustomField<T extends keyof CustomFieldSettingsMap> = Omit<
	CustomField<T>,
	| 'shortName'
	| 'icon'
	| 'appRule'
	| 'displayInBookingList'
	| 'displayInBookingPreview'
	| 'displayOnSession'
	| 'archivedAt'
>;

export type WidgetBookingGiftcard = {
	id: string;
	realPrice: number;
	onlinePrice: number;
	label: string;
	cardColor: string;
	description: string;
};

export type WidgetActivity = {
	id: string;
	name: string;
	from: string | null;
	to: string | null;
	minPax: number;
	maxPax: number;
	image?: string;
};

export type WidgetOffer = {
	id: string;
	name: string;
	sortOrder: number;
	conditions: string;
	shortDescription: string;
	image?: string;
	from: string | null;
	to: string | null;
	minPax: number;
	maxPax: number;
	activityIds: Array<string>;
	collectionId: WidgetOfferCollection['id'];
};

export type WidgetOfferCollection = {
	id: string;
	name: string;
	sortOrder: number;
	description: string;
	image?: string;
	activityIds: Array<string>;
	offerIds: Array<string>;
};

export type WidgetCustomer = {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	phoneOptIn: boolean;
	emailOptIn: boolean;
	customFields: Record<WidgetCustomField<keyof CustomFieldSettingsMap>['id'], string>;
};

export type WidgetSession = {
	name: string;
	durationByPax: Record<string, number>;
	activityId: WidgetActivity['id'];
};

export type PriceTimePeriod = {
	start: number;
	end: number;
};
export type WidgetPrice = {
	id: string;
	name: string;
	description: string;
	minPax: number;
	maxPax: number;
	validMinPax: number;
	validMaxPax: number;
	price: number;
	timePeriods: Record<string, PriceTimePeriod>;
};

type GuestPriceSummary = {
	name: string;
	quantity: number;
	unitPrice: number;
	total: number;
};

type BasePriceSummary = {
	name: string;
	price: number;
};

export type PriceSummary = {
	basePrices?: BasePriceSummary[];
	guestPrices?: GuestPriceSummary[];
	total?: number;
};

export type BillingOptions = {
	fullPayment?: {
		total: number;
	};
	deposit?: {
		total: number;
	};
	payOnsite?: {
		total: number;
	};
	acceptGiftcards: boolean;
};

export type Payment = {
	amount: number;
	refunded: number;
	date: string;
};

export type WidgetPayment = {
	summary: PriceSummary;
	billingOptions: BillingOptions;
	cancellationTimeBefore: number | null;
};

export type BookingSession = {
	id: string;
	activityId: string;
	startAt: number;
	endAt: number;
};

export function upsertBookingIntent(): Promise<void> | undefined {
	const booking: WidgetBooking = get(widgetBooking);
	if (!booking.activity || !booking.pax || !booking.date || !booking.offer) return;

	// Get date as YYYY-MM-DD
	const date = booking.date;
	const formattedDate = date.toISODate();

	const payload = {
		bookingId: booking.id,
		pax: booking.pax,
		date: formattedDate,
		sessions: booking.sessions.map((session) => {
			return {
				id: session.id,
				startAt: session.startAt
			};
		}),
		offerId: booking.offer?.id,
		note: booking.note,
		customer: !booking.customer.firstName
			? undefined
			: {
					firstName: booking.customer.firstName,
					lastName: booking.customer.lastName,
					email: booking.customer.email,
					phone: booking.customer.phone,
					phoneOptIn: booking.customer.phoneOptIn,
					emailOptIn: booking.customer.emailOptIn,
					customFields: booking.customer.customFields
				},
		prices: booking.prices,
		customFields: !booking.customer.firstName ? undefined : booking.customFields
	};

	return (get(apiStore) as Axios)
		.post<{
			bookingId: string;
		}>(`/widget/${get(widgetConfig).hash}/booking`, payload)
		.then((result) => {
			if (result?.data && !booking.id) {
				widgetBooking.set({
					...booking,
					id: result.data.bookingId
				});
				widgetBookingExpiresAt.set(DateTime.now().plus({ minutes: 14 }));
			}
		});
}

export let bookingIsLoading = false;
export const loadBookingIntent = (id: string): Promise<void> | undefined => {
	if (bookingIsLoading) return;
	bookingIsLoading = true;
	const widgetHash = get(widgetConfig).hash;

	type GetWidgetBookingResponse = {
		id: string;
		pax: number;
		date: string;
		note: string;
		offerId: string;
		customer: {
			firstName: string;
			lastName: string;
			email: string;
			phone: string;
			customFields: Record<string, string>;
		};
		status: BookingStatus;
		sessions: BookingSession[];
		customFields: Record<string, string>;
		prices: Record<string, number>;
	};

	return (get(apiStore) as Axios)
		.get<GetWidgetBookingResponse>(`/widget/${widgetHash}/booking/${id}`)
		.then((result) => {
			if (result?.data) {
				const offer = get(widgetContent).offers[result.data.offerId];
				const activity = get(widgetContent).activities.find((activity) =>
					offer.activityIds.includes(activity.id)
				);
				widgetBooking.set({
					...result.data,
					date: DateTime.fromISO(result.data.date),
					customer: {
						...result.data.customer,
						phoneOptIn: false,
						emailOptIn: false,
						customFields:
							Array.isArray(result.data.customer.customFields) &&
							result.data.customer.customFields.length === 0
								? {}
								: result.data.customer.customFields
					},
					customFields:
						Array.isArray(result.data.customFields) &&
						result.data.customFields.length === 0
							? {}
							: result.data.customFields,
					activity,
					offer
				});
				bookingIsLoading = false;
			}
		})
		.catch((e) => {
			console.error(e);
			throw e;
		});
};

export function unlinkAndRefundGiftcards(): Promise<void> | undefined {
	return get(apiStore)?.delete(
		'/widget/' + get(widgetConfig).hash + '/giftcard/' + get(widgetBooking).id + '/unlink'
	);
}

export function bookingValidateFullGiftcardPayment(
	paymentType: 'deposit' | 'full'
): Promise<void> | undefined {
	return get(apiStore)?.post(
		'/widget/' +
			get(widgetConfig).hash +
			'/giftcard/' +
			get(widgetBooking).id +
			'/' +
			paymentType +
			'/paid'
	);
}

export function bookingGetStartTime(booking: WidgetBooking): number | undefined {
	if (booking.sessions.length === 0) return undefined;

	// Find the first session
	return Math.min(...booking.sessions.map((session) => session.startAt));
}

export function getRoundedClasses(
	themeButtonStyle: WidgetButtonStyle,
	rounded: WidgetButtonStyle | 'auto' = 'auto'
) {
	let buttonStyle = themeButtonStyle;
	if (rounded !== 'auto') {
		buttonStyle = rounded;
	}

	switch (buttonStyle) {
		case 'sharp':
			return 'rounded-none';
		case 'rounded':
			return 'rounded-md';
		case 'circle':
			return 'rounded-full';
		default:
			return 'rounded-md';
	}
}

export function computeTotal(booking: WidgetBooking, prices: WidgetPrice[]) {
	let total = 0;
	Object.entries(booking.prices).forEach(([priceId, quantity]) => {
		const price = prices.find((price) => price.id === priceId);
		if (price) {
			total += price.price * quantity;
		}
	});
	return total;
}

export const inputStyleOptions = {
	bg: 'bg-widget-background dark:bg-widget-background-dark',
	textColor: 'text-widget-background-on dark:text-widget-background-dark-on',
	border: 'border border-widget-primary/70 dark:border-widget-primary-dark/30 focus:border-widget-primary dark:focus:border-widget-primary'
};

export type WidgetGiftcardForm = {
	giftcard: WidgetBookingGiftcard;
	quantity: number;
	sendDate: DateTime | undefined;
	senderName: string;
	senderEmail: string;
	recipients: Recipient[];
	message: string;
	allowOptIn: boolean;
};

export type Recipient = {
	name: string;
	email: string;
};

export function getWidgetTermsHtml(): string {
	return get(t)(
		get(widgetConfig).termsLink || get(widgetConfig).termsContent
			? 'widget-external.customer.termsAcceptation'
			: 'widget-external.customer.termsAcceptation_reservatoo',
		{
			reservatoo_terms_link: '/terms',
			business_terms_link: get(widgetConfig).termsLink
				? get(widgetConfig).termsLink!
				: `/widget/${get(widgetConfig).hash}/terms`
		}
	);
}

export function getDuration(pax: number, sessionId: Session['id']): number | undefined {
	let out = undefined;
	Object.keys(get(widgetContent).sessions[sessionId].durationByPax).forEach((paxRange) => {
		const [min, max] = paxRange.split('-').map(Number);
		if (pax >= min && pax <= max) {
			out = get(widgetContent).sessions[sessionId].durationByPax[paxRange];
		}
	});
	return out;
}
